import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  NhsVoucherRate,
  PcseVoucherSupplement,
  PcseVoucherType,
  VoucherQuote,
  VoucherQuoteRequest,
} from '../../@types/pcse';
import { pcseService } from '../../api/pcse.service';

/**
 * NHS voucher values for the claim/dispense UI.
 *
 * There is deliberately NO rate table in this file. The values are set by NHS
 * England and change after negotiation, so they live in `nhs_voucher_rate` on
 * the server and are fetched. A mirrored table here would go stale silently and
 * quote patients a figure the practice could not actually claim — which is
 * exactly what the previous £0 placeholder table did.
 *
 * Labels below are presentation only; every number comes from the server.
 */

/** Display labels for the supplements a dispenser can tick. */
export const VOUCHER_SUPPLEMENTS: Record<
  PcseVoucherSupplement,
  { label: string }
> = {
  PRISM: { label: 'Prism' },
  TINT: { label: 'Tint' },
  PHOTOCHROMIC: { label: 'Photochromic' },
  SPECIAL_CATEGORY: { label: 'Special facial characteristics' },
  SMALL_GLASSES: { label: 'Small glasses' },
  COMPLEX_LENS: { label: 'Complex lens' },
};

/**
 * NHS eligibility reasons — the boolean flags of the shared `NhsEligibility`
 * model (patient-nhs entity). Rendered as checkboxes on the claim form so the
 * form writes back to the same eligibility structure used elsewhere.
 */
export const NHS_ELIGIBILITY_FLAGS: {
  key: keyof import('../../@types/pcse').NhsEligibility;
  label: string;
}[] = [
  { key: 'isUnder16', label: 'Aged under 16' },
  { key: 'isOver60', label: 'Aged 60 or over' },
  { key: 'isStudent', label: 'Full-time student (16–18)' },
  { key: 'isBenefits', label: 'Receiving qualifying benefits' },
  { key: 'isGlaucomaDiabetes', label: 'Diagnosed glaucoma or diabetes' },
  { key: 'isFamilyHistory', label: 'Family history of glaucoma' },
];

export interface VoucherTypeOption {
  value: PcseVoucherType;
  label: string;
  valueGBP: number;
}

const BAND_CODES: PcseVoucherType[] = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
];

/**
 * Load the voucher values in force, and build the band dropdown from them.
 *
 * `onDate` should be the date the appliance is supplied when that is known, so
 * a dispense collected in a previous rate period is priced at the rates that
 * actually apply to it.
 */
export function useNhsVoucherRates(onDate?: string) {
  const [rates, setRates] = useState<NhsVoucherRate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    pcseService
      .listVoucherRates(onDate)
      .then((res) => {
        if (cancelled) return;
        setRates(res.data ?? []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setRates([]);
        setError(
          err?.message ??
            'Could not load NHS voucher values. Check them under Settings → NHS voucher rates.',
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [onDate]);

  const byCode = useMemo(
    () => new Map(rates.map((r) => [r.code, r])),
    [rates],
  );

  const typeOptions = useMemo<VoucherTypeOption[]>(
    () =>
      BAND_CODES.filter((code) => byCode.has(code)).map((code) => {
        const rate = byCode.get(code)!;
        return {
          value: code,
          label: `Type ${code} — ${rate.description ?? ''} (£${Number(
            rate.valueGBP,
          ).toFixed(2)})`,
          valueGBP: Number(rate.valueGBP),
        };
      }),
    [byCode],
  );

  /** True when the table is missing or every band is £0 — nothing claimable. */
  const unconfigured =
    !loading && typeOptions.every((o) => !o.valueGBP);

  return { rates, byCode, typeOptions, loading, error, unconfigured };
}

/**
 * Server-computed voucher quote for the current form state.
 *
 * The UI never prices a voucher itself: this is the same calculation that later
 * builds the GOS3 claim, so what the patient is quoted and what PCSE is billed
 * cannot drift apart. Requests are debounced because it fires on every keystroke
 * in the amount field.
 */
export function useVoucherQuote(
  request: VoucherQuoteRequest | null,
  debounceMs = 300,
) {
  const [quote, setQuote] = useState<VoucherQuote | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Serialised so the effect compares by value, not by object identity.
  const key = request ? JSON.stringify(request) : null;
  const latest = useRef(0);

  const run = useCallback(async (payload: VoucherQuoteRequest, seq: number) => {
    try {
      setLoading(true);
      const res = await pcseService.quoteVoucher(payload);
      // Ignore a slow response that a newer request has already superseded.
      if (seq !== latest.current) return;
      setQuote(res.data);
      setError(null);
    } catch (err: any) {
      if (seq !== latest.current) return;
      setQuote(null);
      setError(err?.message ?? 'Could not value the voucher.');
    } finally {
      if (seq === latest.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!key) {
      setQuote(null);
      setLoading(false);
      return undefined;
    }

    const seq = ++latest.current;
    const timer = setTimeout(() => run(JSON.parse(key), seq), debounceMs);
    return () => clearTimeout(timer);
  }, [key, debounceMs, run]);

  return { quote, loading, error };
}
